<template>
  <div class="flex flex-col">
    <div>
      <lf-h3>{{ $t("ORCHESTRATION.ORCHESTRATION_OUTCOME") }}</lf-h3>
    </div>
    <div class="flex items-center space-x-1">
      <span>{{ $t("COMMON.TEMPLATE_NAME") }}:</span>
      <a
        v-if="canViewDO"
        :href="templateLink"
        :class="{
          'pointer-events-none': !activeDataOrchestrationLog?.template,
        }"
        target="_blank"
        rel="noopener noreferrer"
        class="text-gray-600 hover:underline"
        data-cy="do-template-link"
      >
        {{ statusTitle }}
      </a>
      <span v-else class="font-normal text-gray-600" data-cy="do-template-name">
        {{ statusTitle }}
      </span>
      <div
        v-if="showOrchHistory"
        class="cursor-pointer p-2"
        @click="
          $emit('update:orchestrationHistoryOpen', !orchestrationHistoryOpen)
        "
      >
        <icon-chevron-up
          class="transform transition-transform"
          :class="{ 'rotate-180': !orchestrationHistoryOpen }"
        />
      </div>
    </div>
    <div
      v-if="activeDataOrchestrationLog"
      class="flex items-center space-x-2"
      data-cy="do-date-last-run"
    >
      <span>{{ $t("COMMON.LAST") }} {{ $t("COMMON.RUN") }}:</span>
      <span class="font-normal text-gray-600">
        {{ lastRunDate }}
      </span>
    </div>
    <span v-if="internalError" class="break-words text-red-600">
      {{ internalError }}
    </span>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { Ability, PermissionSubject } from "@/enums/auth";
import { DATA_ORCHESTRATION_MASTER_STATUSES } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";
import { usePermissions } from "@/hooks/auth";
import { useUnderwritingLogs } from "@/hooks/underwriting";
import useApplicationsStore from "@/stores/applications";

import IconChevronUp from "@/components/icons/deals/IconChevronUp.vue";

const ORCHESTRATION_PATH = "/builders/orchestration/";
const baseURL = window.location.origin;

defineProps({
  orchestrationHistoryOpen: {
    type: Boolean,
    required: true,
  },
  showOrchHistory: {
    type: Boolean,
    required: true,
  },
});

defineEmits<{
  (eventName: "update:orchestrationHistoryOpen", value: boolean): void;
}>();

const canViewDO = ref(false);

const {
  internalError,
  masterStatus,
  newestAutomatedHistoryLog,
  isOrchestrationPaused,
} = useUnderwritingLogs();
const { t } = useI18n();
const applicationsStore = useApplicationsStore();
const { activeDataOrchestrationLog } = storeToRefs(applicationsStore);
const { checkPermissions } = usePermissions();

const templateLink = computed(() => {
  return (
    baseURL +
    ORCHESTRATION_PATH +
    activeDataOrchestrationLog.value?.template?.id
  );
});

const isInProgress = computed(
  () => masterStatus.value === DATA_ORCHESTRATION_MASTER_STATUSES.PROCESSING
);
const statusTitle = computed(() => {
  const templateName = activeDataOrchestrationLog.value?.template?.name;
  if (isInProgress.value) {
    return templateName || t("ORCHESTRATION.IN_PROGRESS");
  }
  if (
    newestAutomatedHistoryLog.value?.outcome ||
    newestAutomatedHistoryLog.value?.context?.master_status
  ) {
    return templateName || t("ORCHESTRATION.OUTCOME_STATUS");
  }
  if (isOrchestrationPaused.value) {
    return templateName || t("ORCHESTRATION.PAUSED");
  }
  if (internalError.value) {
    return templateName || t("COMMON.ERROR");
  }
  return t("ORCHESTRATION.NOT_STARTED");
});

const lastRunDate = computed(() =>
  formatDateCustom(
    activeDataOrchestrationLog.value?.started_at,
    "MMMM dd, yyyy hh:mm a",
    true
  )
);

const permissionViewDO = async () => {
  canViewDO.value = await checkPermissions(
    PermissionSubject.dataOrchestrationTemplate,
    Ability.view,
    {
      template: activeDataOrchestrationLog.value?.template?.id,
    }
  );
};

watch(
  () => activeDataOrchestrationLog.value?.template?.id,
  () => permissionViewDO(),
  { immediate: true }
);
</script>
